// Central manager for session events: why the user was logged out and
// where to navigate back to after re-authentication.
// Pure browser utilities — no React dependencies.

const RETURN_KEY = 'session:return_to';
const REASON_KEY = 'session:logout_reason';
const STALE_MS   = 30 * 60 * 1000; // locations older than 30 min are discarded

// Paths that should never be saved as a return location.
const SKIP_PATHS = [
  '/login', '/forgot-password', '/reset-password',
  '/400', '/401', '/403', '/404', '/500',
];

// ── Write ──────────────────────────────────────────────────────────────────

// Call BEFORE clearing the token. Skips auth/error pages.
// sessionStorage is tab-scoped so multiple tabs don't interfere.
export const saveReturnLocation = () => {
  const { pathname, search, hash } = window.location;
  if (SKIP_PATHS.some(p => pathname.startsWith(p))) return;
  sessionStorage.setItem(RETURN_KEY, JSON.stringify({
    pathname, search, hash, ts: Date.now(),
  }));
};

export const setLogoutReason = (reason) => {
  sessionStorage.setItem(REASON_KEY, reason);
};

// ── Read (one-shot: clears on read to prevent loops) ──────────────────────

export const consumeLogoutReason = () => {
  const reason = sessionStorage.getItem(REASON_KEY);
  sessionStorage.removeItem(REASON_KEY);
  return reason || null;
};

export const consumeReturnLocation = () => {
  const raw = sessionStorage.getItem(RETURN_KEY);
  sessionStorage.removeItem(RETURN_KEY);
  if (!raw) return null;
  try {
    const saved = JSON.parse(raw);
    if (!saved?.pathname) return null;
    // Stale location — session ended too long ago to be useful
    if (Date.now() - saved.ts > STALE_MS) return null;
    // Never navigate back to auth or error pages
    if (SKIP_PATHS.some(p => saved.pathname.startsWith(p))) return null;
    // Admin-only routes: only restore if user is actually a superadmin
    if (saved.pathname.startsWith('/administrator/') &&
        localStorage.getItem('is_superadmin') !== 'true') return null;
    return { pathname: saved.pathname, search: saved.search || '', hash: saved.hash || '' };
  } catch {
    return null;
  }
};

// ── Toast config by reason ────────────────────────────────────────────────

// Returns null for intentional (manual) logouts — no toast needed.
export const getLogoutToast = (reason) => {
  switch (reason) {
    case 'session_expired':
      return {
        type: 'warning',
        title: 'Session Expired',
        message: 'Your session expired due to inactivity. Please sign in again to continue.',
        duration: 8000,
      };
    case 'token_invalid':
      return {
        type: 'error',
        title: 'Authentication Failed',
        message: 'Your session token is no longer valid. Please sign in again.',
        duration: 7000,
      };
    case 'forbidden':
      return {
        type: 'error',
        title: 'Access Denied',
        message: 'Access was denied. Please sign in with an account that has the required permissions.',
        duration: 7000,
      };
    default:
      return null;
  }
};
